import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import type { RouterStateSnapshot } from '@angular/router';
import { LoginService } from '../../auth/login.service';
import { loginNavigationExtras } from '../../auth/safe-return-url';

/**
 * Pure 2FA check — safe to call from AuthGuard's ensureSession map
 * (outside inject() context). TwoFactorGuard is the CanActivateFn wrapper.
 */
export function evaluateTwoFactorAccess(
  loginService: LoginService,
  router: Router,
  state: RouterStateSnapshot
): boolean {
  if (!loginService.isAuthenticated()) {
    void router.navigate(['/login'], loginNavigationExtras(state.url));
    return false;
  }

  const user = loginService.getUser();
  if (!user) {
    void router.navigate(['/login'], loginNavigationExtras(state.url));
    return false;
  }

  // Allow access to profile page so user can enable 2FA
  if (state.url === '/profile' || state.url.startsWith('/profile')) {
    return true;
  }

  if (!user.totpEnabled) {
    router.navigate(['/profile'], {
      queryParams: { require2FA: 'true' },
    });
    return false;
  }

  return true;
}

/**
 * Guard that enforces 2FA requirement.
 * Allows access to profile page (to enable 2FA) but blocks all other routes
 * if user doesn't have 2FA enabled.
 */
export const TwoFactorGuard: CanActivateFn = (_route, state) => {
  return evaluateTwoFactorAccess(inject(LoginService), inject(Router), state);
};
